import { baseApi } from './baseApi';

export const salesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSalesOrders: builder.query({
      query: (params) => ({
        url: '/sales/orders',
        params,
      }),
      providesTags: ['Sales'],
    }),
    getSalesOrder: builder.query({
      query: (id) => `/sales/orders/${id}`,
      providesTags: (result, error, id) => [{ type: 'Sales', id }],
    }),
    createSalesOrder: builder.mutation({
      query: (data) => ({
        url: '/sales/orders',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Sales', 'Inventory', 'RegisterSession'],
    }),
    deleteSalesOrder: builder.mutation({
      query: (id) => ({
        url: `/sales/orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sales', 'Inventory', 'Report', 'RegisterSession'],
    }),
    updateSalesOrderStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/sales/orders/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => ['Sales', { type: 'Sales', id }],
    }),
    holdSalesOrder: builder.mutation({
      query: (id) => ({
        url: `/sales/orders/${id}/hold`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => ['Sales', { type: 'Sales', id }],
    }),
    unholdSalesOrder: builder.mutation({
      query: (id) => ({
        url: `/sales/orders/${id}/unhold`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => ['Sales', { type: 'Sales', id }],
    }),
    getHolds: builder.query({
      query: (params) => ({
        url: '/sales/holds',
        params: params || {},
      }),
      providesTags: ['SalesHolds'],
    }),
    getHold: builder.query({
      query: (id) => `/sales/holds/${id}`,
      providesTags: (result, error, id) => [{ type: 'SalesHolds', id }],
    }),
    createHold: builder.mutation({
      query: (data) => ({
        url: '/sales/holds',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SalesHolds'],
    }),
    updateHold: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/sales/holds/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ['SalesHolds', { type: 'SalesHolds', id }],
    }),
    deleteHold: builder.mutation({
      query: (id) => ({
        url: `/sales/holds/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesHolds'],
    }),
    unholdHold: builder.mutation({
      query: (id) => ({
        url: `/sales/holds/${id}/unhold`,
        method: 'POST',
      }),
      invalidatesTags: ['SalesHolds', 'Sales', 'Inventory'],
    }),
    getCustomers: builder.query({
      query: (params) => ({
        url: '/sales/customers',
        params,
      }),
      providesTags: ['Sales', 'Customer'],
    }),
    getCustomer: builder.query({
      query: (id) => `/sales/customers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),
    getCustomerHistory: builder.query({
      query: (id) => `/sales/customers/${id}/history`,
      providesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),
    createCustomer: builder.mutation({
      query: (data) => ({
        url: '/sales/customers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Sales', 'Customer'],
    }),
    updateCustomer: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/sales/customers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Sales', 'Customer'],
    }),
    toggleCustomerStatus: builder.mutation({
      query: (id) => ({
        url: `/sales/customers/${id}/status`,
        method: 'POST',
      }),
      invalidatesTags: ['Sales', 'Customer'],
    }),
    recordSalesPayment: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sales/orders/${id}/payments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        'Sales',
        { type: 'Sales', id },
        'Report',
        'RegisterSession',
      ],
    }),
    recordCustomerPayment: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sales/customers/${id}/payments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        'Sales',
        'Customer',
        { type: 'Customer', id },
        'Report',
        'RegisterSession',
      ],
    }),
    deleteCustomerPayment: builder.mutation({
      query: ({ customerId, paymentId }) => ({
        url: `/sales/customers/${customerId}/payments/${paymentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { customerId }) => [
        'Sales',
        'Customer',
        { type: 'Customer', id: customerId },
        'Report',
        'RegisterSession',
      ],
    }),
    allocateCustomerAdvance: builder.mutation({
      query: ({ id, amount }) => ({
        url: `/sales/customers/${id}/allocate-advance`,
        method: 'POST',
        body: { amount },
      }),
      invalidatesTags: (result, error, { id }) => [
        'Sales',
        'Customer',
        { type: 'Customer', id },
        'Report',
        'RegisterSession',
      ],
    }),
    getSalesReturns: builder.query({
      query: (params) => ({
        url: '/sales/returns',
        params,
      }),
      providesTags: ['SalesReturn'],
    }),
    getSalesReturn: builder.query({
      query: (id) => `/sales/returns/${id}`,
      providesTags: (result, error, id) => [{ type: 'SalesReturn', id }],
    }),
    createSalesReturn: builder.mutation({
      query: (data) => ({
        url: '/sales/returns',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SalesReturn', 'Sales', 'Report', 'Inventory', 'RegisterSession'],
    }),
    getCustomerProductHistory: builder.query({
      query: ({ customerId, productId }) => ({
        url: '/sales/returns/history',
        params: { customerId, productId },
      }),
    }),
    deleteSalesReturn: builder.mutation({
      query: (id) => ({
        url: `/sales/returns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesReturn', 'Sales', 'Report', 'Inventory'],
    }),
    sendInvoiceEmail: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sales/orders/${id}/email`,
        method: 'POST',
        body,
      }),
    }),
    sendInvoiceWhatsapp: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sales/orders/${id}/whatsapp`,
        method: 'POST',
        body,
      }),
    }),
    printInvoiceNetwork: builder.mutation({
      query: (id) => ({
        url: `/sales/orders/${id}/print-network`,
        method: 'POST',
      }),
    }),
    getActiveRegisterSession: builder.query({
      query: () => '/sales/register-session/active',
      providesTags: ['RegisterSession'],
    }),
    openRegisterSession: builder.mutation({
      query: (body) => ({
        url: '/sales/register-session/open',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RegisterSession'],
    }),
    closeRegisterSession: builder.mutation({
      query: (body) => ({
        url: '/sales/register-session/close',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RegisterSession', 'Sales'],
    }),
  }),
});

export const {
  useGetSalesOrdersQuery,
  useLazyGetSalesOrdersQuery,
  useGetSalesOrderQuery,
  useLazyGetSalesOrderQuery,
  useCreateSalesOrderMutation,
  useDeleteSalesOrderMutation,
  useUpdateSalesOrderStatusMutation,
  useHoldSalesOrderMutation,
  useUnholdSalesOrderMutation,
  useGetHoldsQuery,
  useGetHoldQuery,
  useCreateHoldMutation,
  useUpdateHoldMutation,
  useDeleteHoldMutation,
  useUnholdHoldMutation,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useGetCustomerHistoryQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useToggleCustomerStatusMutation,
  useRecordSalesPaymentMutation,
  useRecordCustomerPaymentMutation,
  useDeleteCustomerPaymentMutation,
  useAllocateCustomerAdvanceMutation,
  useGetSalesReturnsQuery,
  useGetSalesReturnQuery,
  useCreateSalesReturnMutation,
  useDeleteSalesReturnMutation,
  useGetCustomerProductHistoryQuery,
  useLazyGetCustomerProductHistoryQuery,
  useSendInvoiceEmailMutation,
  useSendInvoiceWhatsappMutation,
  usePrintInvoiceNetworkMutation,
  useGetActiveRegisterSessionQuery,
  useOpenRegisterSessionMutation,
  useCloseRegisterSessionMutation,
} = salesApi;
